import { useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import { connectPackageFrame } from './connectPackageFrame';
import {
  KC_HOME_PATH,
  KC_NAVIGATOR_PATH,
  KC_TOOLS_PATH,
  getNavigatorPackageBySlug,
  getRelatedNavigatorPackages,
} from './repository';
import { KC_COLORS } from './theme';
import { KC_TRUNCATE, truncateAtWord } from './truncateAtWord';
import { NotFound } from '../notFound/NotFound';
import './knowledgeCenter.css';

export function KnowledgeCenterPackagePage() {
  const { packageSlug } = useParams();
  const navigate = useNavigate();
  const frameRef = useRef(null);
  const item = getNavigatorPackageBySlug(packageSlug);

  useEffect(() => {
    const iframe = frameRef.current;
    if (!iframe || !item?.embedHref) return undefined;

    let disconnect = () => {};
    const bind = () => {
      disconnect();
      disconnect = connectPackageFrame(iframe, (path) => {
        navigate(path);
      });
    };

    iframe.addEventListener('load', bind);
    if (iframe.contentDocument?.readyState === 'complete') {
      bind();
    }

    return () => {
      iframe.removeEventListener('load', bind);
      disconnect();
    };
  }, [item?.embedHref, item?.slug, navigate]);

  if (!item) return <NotFound />;

  const isTool = item.resource_type === 'Tool';
  const listPath = isTool ? KC_TOOLS_PATH : KC_NAVIGATOR_PATH;
  const listLabel = isTool ? 'Tools' : 'Navigator';
  const relatedPackages = getRelatedNavigatorPackages(item).slice(0, 6);

  return (
    <>
      <PageSeoHelmet
        pageTitle={`${item.title} | Charlee.ai Knowledge Center`}
        metaDescription={item.purpose}
        canonicalPath={item.detailPath}
      />
      <div className="kc-ds kc-pkg-shell">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain className="kc-pkg-main">
          <div className="kc-pkg-topbar">
            <div className="kc-pkg-wide">
              <div className="kc-pkg-topbar-inner">
                <nav className="kc-pkg-crumb" aria-label="Breadcrumb">
                  <Link to={KC_HOME_PATH}>Knowledge Center</Link>
                  <span>/</span>
                  <Link to={listPath}>{listLabel}</Link>
                  <span>/</span>
                  <span className="kc-pkg-crumb-current">{item.title}</span>
                </nav>
                <Link to={listPath} className="kc-pkg-back">
                  <ArrowBackRoundedIcon sx={{ fontSize: 18 }} />
                  Back to {listLabel}
                </Link>
              </div>
            </div>
          </div>
          {item.embedHref ? (
            <div className="kc-pkg-wide kc-pkg-frame-wrap">
              <iframe
                ref={frameRef}
                title={item.title}
                src={item.embedHref}
                    className="kc-pkg-iframe kc-pkg-iframe--page"
              />
            </div>
          ) : (
            <div className="kc-pkg-wide">
              <p className="kc-pkg-missing">This package does not have an HTML view.</p>
            </div>
          )}
          {relatedPackages.length > 0 ? (
            <div className="kc-pkg-wide kc-pkg-related-reading">
              <div className="kc-related-reading">
                <h2
                  className="kc-related-reading-title kc-type-h2 m-0"
                  style={{ color: KC_COLORS.primaryNavy }}
                >
                  Related Reading
                </h2>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3.5 list-none p-0 m-0 items-stretch">
                  {relatedPackages.map((related) => (
                    <li key={related.id} className="h-full">
                      <Link
                        to={related.detailPath}
                        className="kc-related-card group h-full no-underline"
                      >
                        <span className="kc-related-card-id">{related.id}</span>
                        <span className="kc-related-card-heading">
                          <span>
                            {truncateAtWord(
                              related.title,
                              KC_TRUNCATE.relatedTitle
                            )}
                          </span>
                        </span>
                        <span className="kc-related-card-time">
                          {related.resource_type}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ) : null}
        </PageMain>
      </div>
    </>
  );
}
